import axios from 'axios';
import { modal } from '../modal-template/modal-template';

const DATA_ATTR = 'data-account-cabinet-group-info';
const CLASS_PREFIX = 'account-cabinet-group-info';

/**
 * Инициализация блока "group info"
 * @param {HTMLElement} $container - контейнер, куда будет вставлен шаблон
 * @param {number|string} group_id
 * @returns {{ refresh: Function, destroy: Function }}
 */
export function initPersonalAccountGroup($container, group_id) {
    // template nodes (найдутся при старте)
    const $templateMain = document.querySelector(`[${DATA_ATTR}-template-main]`);
    const $templateTask = document.querySelector(`[${DATA_ATTR}-template-task]`);
    const $templateEmpty = document.querySelector(`[${DATA_ATTR}-template-empty]`); // опционально

    // DOM-поля, инициализируются в renderMain
    let $mainRoot = null;
    let $tableBody = null;
    let $groupCount = null;
    let $copyReferalBtn = null;
    let $btnBack = null;
    let $loader = null

    // pagination DOM
    let $paginationRoot = null;
    let $paginationContainer = null;
    let $paginationPrev = null;
    let $paginationNext = null;

    // state
    let currentSkip = 0;
    let currentTake = 10; // по умолчанию 10 — поменяй здесь, если нужно другое значение
    let referalLink = '';
    let destroyed = false;
    let isLoading = false;

    // привязки для removeEventListener
    const handlers = {
        onBack: null,
        onCopyReferral: null,
        onPrevClick: null,
        onNextClick: null
    };

    // Guard: если шаблонов нет — аккуратно выйти
    if (!$templateMain || !$templateTask) {
        console.warn(`[${CLASS_PREFIX}] templates not found, init aborted.`);
        return {
            refresh: () => Promise.resolve(),
            destroy: () => {}
        };
    }

    // Сделаем публичные методы
    const api = {
        refresh: fetchGroup,
        destroy
    };

    // Запуск инициализация
    init();

    /* ========== Implementation ========== */
    async function init() {
        if (destroyed) return;
        try { window.CURRENT_GROUP_ID = group_id; } catch (e) {}

        await renderMain();
        fetchGroup();

        document.addEventListener('account-task:created', handleTaskCreated);
    }

    async function fetchGroup() {
        if (destroyed) return;
        if (isLoading) return;
        isLoading = true;
        setLoadingState(true);
        $loader.style.display = 'flex';

        try {
            const resp = await axios.get(`/api/group/${group_id}`, {
                params: { skip: currentSkip, take: currentTake }
            });

            if (destroyed) return;

            const { group = {}, meta = {}, tasks = [] } = resp.data || {};

            setGroupCount(group.students_count);
            setReferral(group.referral_code);

            renderList(tasks, meta || {});

            $loader.style.display = 'none';
        } catch (err) {
            console.error(`[${CLASS_PREFIX}] fetchGroup error:`, err);
            renderEmpty(); // graceful fallback
        } finally {
            isLoading = false;
            setLoadingState(false);
        }
    }

    function bindEvents() {
        if ($btnBack) {
            handlers.onBack = (e) => {
                e.preventDefault();
                document.dispatchEvent(new CustomEvent('account-cabinet:open', {
                    detail: { name: 'group-config' }
                }));
            };
            $btnBack.addEventListener('click', handlers.onBack);
        }

        if ($copyReferalBtn) {
            handlers.onCopyReferral = (e) => {
                e.preventDefault();
                modal.open('copy-referal', { modalReferral: referalLink });
            };
            $copyReferalBtn.addEventListener('click', handlers.onCopyReferral);
        }

        if ($paginationPrev) {
            handlers.onPrevClick = (e) => {
                e.preventDefault();
                goToPrevious();
            };
            $paginationPrev.addEventListener('click', handlers.onPrevClick);
        }

        if ($paginationNext) {
            handlers.onNextClick = (e) => {
                e.preventDefault();
                goToNext();
            };
            $paginationNext.addEventListener('click', handlers.onNextClick);
        }
    }
    function unbindEvents() {
        if ($btnBack && handlers.onBack) {
            $btnBack.removeEventListener('click', handlers.onBack);
            handlers.onBack = null;
        }
        if ($copyReferalBtn && handlers.onCopyReferral) {
            $copyReferalBtn.removeEventListener('click', handlers.onCopyReferral);
            handlers.onCopyReferral = null;
        }
        if ($paginationPrev && handlers.onPrevClick) {
            $paginationPrev.removeEventListener('click', handlers.onPrevClick);
            handlers.onPrevClick = null;
        }
        if ($paginationNext && handlers.onNextClick) {
            $paginationNext.removeEventListener('click', handlers.onNextClick);
            handlers.onNextClick = null;
        }

        if ($paginationContainer) {
            // удаляем навешанные клики на кнопках страниц (они добавлялись при рендере)
            const items = $paginationContainer.querySelectorAll(`.${CLASS_PREFIX}__pagination-item`);
            items.forEach(item => {
                item.replaceWith(item.cloneNode(true)); // простой способ убрать слушатели
            });
            $paginationContainer.innerHTML = '';
        }
    }

    function renderMain() {
        const fragment = $templateMain.content.cloneNode(true);
        const root = fragment.querySelector(`[${DATA_ATTR}]`) || fragment.firstElementChild;

        if (!root) {
            console.warn(`[${CLASS_PREFIX}] main template does not contain root [${DATA_ATTR}]`);
            return;
        }

        $mainRoot = root;
        $btnBack = $mainRoot.querySelector(`[${DATA_ATTR}-btn-back]`);
        $tableBody = $mainRoot.querySelector(`[${DATA_ATTR}-table-body]`);
        $groupCount = $mainRoot.querySelector(`[${DATA_ATTR}-group-count]`);
        $copyReferalBtn = $mainRoot.querySelector(`[${DATA_ATTR}-copy-referal]`);

        $loader = $mainRoot.querySelector(`[${DATA_ATTR}-loader]`);

        // pagination elems
        $paginationRoot = $mainRoot.querySelector(`[${DATA_ATTR}-pagination]`);
        $paginationContainer = $mainRoot.querySelector(`[${DATA_ATTR}-pagination-container]`);
        $paginationPrev = $mainRoot.querySelector(`[${DATA_ATTR}-pagination-prev]`);
        $paginationNext = $mainRoot.querySelector(`[${DATA_ATTR}-pagination-next]`);

        bindEvents();

        $container.appendChild(fragment);
        document.dispatchEvent(new CustomEvent('account-cabinet:render-complete', {bubbles: true}));
    }

    function renderEmpty() {
        if ($tableBody) $tableBody.innerHTML = '';
        if ($paginationContainer) $paginationContainer.innerHTML = '';

        if ($groupCount) $groupCount.textContent = '';

        if ($templateEmpty) {
            const frag = $templateEmpty.content.cloneNode(true);
            const containerForEmpty = $mainRoot.querySelector(`[${DATA_ATTR}-empty-container]`) || $tableBody;
            if (containerForEmpty) {
                containerForEmpty.appendChild(frag);
            }
        } else {
            if ($tableBody) {
                const tr = document.createElement('div');
                tr.className = `${CLASS_PREFIX}__table-row`;
                const td = document.createElement('div');
                td.className = `${CLASS_PREFIX}__table-cell`;
                td.textContent = 'Нет записей';
                tr.appendChild(td);
                $tableBody.appendChild(tr);
            }
        }
    }

    function renderList(tasks = [], meta = {}) {
        if (!$tableBody) return;
        $tableBody.innerHTML = '';

        const fragment = document.createDocumentFragment();
        tasks.forEach(task => {
            const node = buildTaskRow(task);
            fragment.appendChild(node);
        });

        $tableBody.appendChild(fragment);

        renderPagination(meta);
    }

    function buildTaskRow(task = {}) {
        const taskFragment = $templateTask.content.cloneNode(true);

        const $name = taskFragment.querySelector(`[${DATA_ATTR}-task-name]`);
        const $trainer = taskFragment.querySelector(`[${DATA_ATTR}-task-trainer]`);
        const $type = taskFragment.querySelector(`[${DATA_ATTR}-task-type]`);
        const $taskEditBtn = taskFragment.querySelector(`[${DATA_ATTR}-task-edit]`);

        if ($taskEditBtn) {
            $taskEditBtn.addEventListener('click', (e) => {
                e.preventDefault();
                modal.open('create-student-task', { task_id: task.id });
            });
        }
        if ($name) $name.textContent = task.title || 'Без названия';
        if ($trainer)
        {
            if (task.subject.kind == 'exercise')
            {
                $trainer.classList.add('link');
                $trainer.addEventListener('click', (e) => {
                    e.preventDefault();
                    window.location.href = `/exercise/${task.subject.exercise_id}`;
                });
            }
            $trainer.textContent = (task.subject && task.subject.title) || '-';
        }
        if ($type) $type.textContent = (task.subject && task.subject.type) || '-';

        if (taskFragment.childElementCount === 1) {
            return taskFragment.firstElementChild;
        }
        return taskFragment;
    }

    // ========== pagination helpers ==========
    function renderPagination(meta = {}) {
        if (! $paginationContainer) return;

        const total = Number(meta.total || 0);
        const take = Number(currentTake || meta.take || 10);

        const totalPages = Math.max(1, Math.ceil(total / take));
        const currentPage = Math.floor((currentSkip || 0) / take) + 1;

        $paginationContainer.innerHTML = '';

        const maxVisible = 7;
        const pages = buildPageWindow(currentPage, totalPages, maxVisible);

        pages.forEach(item => {
            if (item === '...') {
                const ell = document.createElement('div');
                ell.className = `${CLASS_PREFIX}__pagination-ellipsis`;
                ell.textContent = '…';
                $paginationContainer.appendChild(ell);
                return;
            }

            const btn = document.createElement('div');
            btn.className = `${CLASS_PREFIX}__pagination-item`;
            if (item === currentPage) btn.classList.add(`${CLASS_PREFIX}__pagination-item--active`);
            btn.textContent = String(item);
            btn.dataset.page = String(item);
            btn.addEventListener('click', onPageClick);
            $paginationContainer.appendChild(btn);
        });

        if ($paginationPrev) {
            if (currentPage <= 1) {
                $paginationPrev.classList.add(`${CLASS_PREFIX}__pagination-item--disabled`);
            } else {
                $paginationPrev.classList.remove(`${CLASS_PREFIX}__pagination-item--disabled`);
            }
        }
        if ($paginationNext) {
            if (currentPage >= totalPages) {
                $paginationNext.classList.add(`${CLASS_PREFIX}__pagination-item--disabled`);
            } else {
                $paginationNext.classList.remove(`${CLASS_PREFIX}__pagination-item--disabled`);
            }
        }
    }

    function buildPageWindow(current, total, maxVisible) {
        const pages = [];
        if (total <= maxVisible) {
            for (let i = 1; i <= total; i++) pages.push(i);
            return pages;
        }

        const side = Math.floor((maxVisible - 3) / 2);
        const left = Math.max(2, current - side);
        const right = Math.min(total - 1, current + side);

        pages.push(1);

        if (left > 2) {
            pages.push('...');
        }

        for (let i = left; i <= right; i++) {
            pages.push(i);
        }

        if (right < total - 1) {
            pages.push('...');
        }

        pages.push(total);

        return pages;
    }

    function onPageClick(e) {
        const el = e.currentTarget;
        const page = parseInt(el.dataset.page, 10);
        if (Number.isNaN(page)) return;
        goToPage(page);
    }

    function goToPage(page) {
        if (destroyed) return;
        const newSkip = Math.max(0, (page - 1) * currentTake);
        if (newSkip === currentSkip) return;
        currentSkip = newSkip;
        fetchGroup();
    }

    function goToPrevious() {
        if (destroyed) return;
        const page = Math.floor(currentSkip / currentTake) + 1;
        if (page <= 1) return;
        goToPage(page - 1);
    }

    function goToNext() {
        if (destroyed) return;
        const page = Math.floor(currentSkip / currentTake) + 1;
        goToPage(page + 1);
    }

    function setGroupCount(n) {
        if (!$groupCount) return;
        const normalized = Number(n) || 0;
        $groupCount.textContent = `${normalized} ${pluralizeStudents(normalized)}`;
    }
    function setReferral(code) {
        if (!code) {
            referalLink = '';
            return;
        }
        try {
            referalLink = `${window.location.origin}/join/${code}`;
        } catch (e) {
            referalLink = `/join/${code}`;
        }
    }
    function setLoadingState(flag) {
        if (!$mainRoot) return;
        if (flag) {
            $mainRoot.classList.add(`${CLASS_PREFIX}--loading`);
        } else {
            $mainRoot.classList.remove(`${CLASS_PREFIX}--loading`);
        }
    }

    function destroy() {
        destroyed = true;
        document.removeEventListener('account-task:created', handleTaskCreated);
        unbindEvents();
        if ($mainRoot && $mainRoot.parentNode) {
            $mainRoot.parentNode.removeChild($mainRoot);
        }
        $mainRoot = null;
        $tableBody = null;
        $groupCount = null;
        $copyReferalBtn = null;
        $btnBack = null;
        $paginationRoot = null;
        $paginationContainer = null;
        $paginationPrev = null;
        $paginationNext = null;
    }

    function pluralizeStudents(n) {
        n = Number(n) || 0;
        if (n % 10 === 1 && n % 100 !== 11) return 'студент';
        if (n % 10 >= 2 && n % 10 <= 4 && !(n % 100 >= 12 && n % 100 <= 14)) return 'студента';
        return 'студентов';
    }

    function handleTaskCreated(e) {
        if (destroyed) return;

        // Если в event.detail приходит group_id — проверим, что событие для текущей группы
        const detail = e && e.detail;
        if (detail && detail.group_id && String(detail.group_id) !== String(group_id)) {
            return; // не наша группа — игнорируем
        }

        // Если нужно сбрасывать пагинацию на первую страницу — раскомментируй следующую строку:
        currentSkip = 0;

        // Просто рефрешим текущую страницу
        fetchGroup();
    }

    return api;
}
